import { ReactNode, useState } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import BottomTabBar from "./BottomTabBar";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import { useIsMobile } from "@/hooks/use-mobile";
import { Skeleton } from "@/components/ui/skeleton";

interface MainLayoutProps {
  children: ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, isLoading } = useAuth();
  const [location] = useLocation();
  const isMobile = useIsMobile();
  
  // Don't render layout for auth pages
  if (location === "/login" || location === "/signup" || location === "/privacy-policy") {
    return <>{children}</>;
  }
  
  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Skeleton className="h-16 w-16 rounded-full mx-auto" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-32 mx-auto" />
            <Skeleton className="h-4 w-48 mx-auto" />
          </div>
          <div className="flex justify-center gap-1 pt-2">
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="w-2 h-2 rounded-full" />
          </div>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return null;
  }
  
  return (
    <div className="bg-background text-foreground h-screen flex flex-col">
      <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

      <div className="flex flex-1 overflow-hidden">
        {!isMobile && (
          <>
            {/* Backdrop overlay for tablet sidebar */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/40 z-10 lg:hidden transition-opacity duration-300"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
          </>
        )}

        <main className={`flex-1 min-w-0 overflow-y-auto pt-2 lg:pl-72 ${isMobile ? 'pb-20' : 'pb-6'}`}>
          <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {isMobile && <BottomTabBar />}
    </div>
  );
}
